import * as Popover from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import cn from "classnames";
import { countryFlags } from "../../../../constants/location";
import { useTranslation } from "react-i18next";
import { ChangeEvent, useState } from "react";
import SearchIcon from "assets/svgComponents/locatios/SearchIcon";

type Props = {
  isOpenedOptions: boolean;
  setOpenedOptions: (val: boolean) => void;
  locations: { [key: string]: string };
  setLocation: (method: string) => void;
  location: string;
  type?: "location";
};

export const LocationSelector: React.FC<Props> = ({
  isOpenedOptions,
  setOpenedOptions,
  location,
  setLocation,
  locations,
  type,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleClose = () => {
    setOpenedOptions(false);
  };

  const filteredLocations = Object.entries(locations).filter(([key, value]) => {
    const search = searchValue.toLowerCase();
    return (
      key.toLowerCase().includes(search) || value.toLowerCase().includes(search)
    );
  });

  return (
    <Popover.Root open={isOpenedOptions} onOpenChange={setOpenedOptions}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="navigation-link relative z-[1000] p-[0.65vw] justify-center max-lg:size-11  outline-none flex items-center gap-[0.8vw] rounded-2xl"
        >
          <div className="size-[2vw] max-lg:size-[7vw] flex items-center justify-center rounded-lg  bg-white shadow-custom">
            <div>
              {countryFlags[location] ? (
                <div className="flex w-[1.3889vw] h-[1.3889vw]  rounded-full overflow-hidden max-lg:w-[4.2vw] max-lg:h-[4.2vw]">
                  <img
                    src={countryFlags[location]}
                    alt="flag"
                    className="object-cover w-full h-full rounded-full "
                  />
                </div>
              ) : (
                <span className="text-[0.8vw] max-lg:text-[3.2vw] font-luxora-medium">
                  {location}
                </span>
              )}
            </div>
          </div>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="center"
          sideOffset={4}
          className="z-[1000] relative location-container max-lg:mr-3 justify-center font-luxora-regular outline-none flex items-center gap-[0.8vw] rounded-2xl"
          asChild
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            <div className="flex flex-col max-h-[18.95vw] max-w-[17.77vw] max-lg:max-h-[256px] max-lg:max-w-[273px]  relative z-[10001] py-[0.55vw] max-lg:py-2 gap-[0.2778vw] max-lg:gap-1 w-full  pr-[0.55vw] max-lg:pr-2">
              <div className=" relative z-[1002]  ml-[0.55vw] max-lg:ml-2">
                <div
                  className={
                    "  absolute top-1/2 -translate-y-1/2 z-[1004] left-[0.7639vw] max-lg:left-[11px]"
                  }
                >
                  <SearchIcon className="max-lg:w-[24px] max-lg:h-[24px] size-[1.6661vw]" />
                </div>

                <input
                  type="text"
                  value={searchValue}
                  placeholder={t("selectLanguage")}
                  onChange={handleInputChange}
                  className="outline-none location-input relative text-[0.9722vw] max-lg:text-[14px] z-[1003] rounded-[16px] h-[2.7778vw] max-lg:h-[40px] pl-[2.7778vw] max-lg:pl-[40px]"
                />
              </div>

              <div
                className="w-full overflow-y-auto scrollbar  [&::-webkit-scrollbar]:w-[0.2778vw] max-lg:[&::-webkit-scrollbar]:w-[4px]
                  [&::-webkit-scrollbar-track]:rounded-full
                  [&::-webkit-scrollbar-track]:bg-[#F3F3F8]
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:bg-white"
              >
                <motion.ul
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-[0.2778vw] max-lg:gap-1"
                >
                  {!!filteredLocations?.length ? (
                    filteredLocations.map(([key, name], i) => {
                      return (
                        <li
                          className={cn(
                            "location-item mx-[0.55vw] max-lg:mx-2 relative z-[1000] py-[0.2778vw] max-lg:py-1 pl-[0.2778vw] max-lg:pl-1 pr-[0.8333vw] max-lg:pr-3 justify-center  outline-none flex items-center gap-[0.8vw] max-lg:gap-3 rounded-[12px]"
                          )}
                          key={key}
                          onClick={() => {
                            setLocation(key);
                            handleClose();
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-[0.55vw] max-lg:gap-2">
                              <div className="size-[1.9444vw] max-lg:size-[28px] rounded-[8px] bg-white p-[0.2778vw] max-lg:p-1 gap-[0.55vw] max-lg:gap-2">

                                <img
                                  src={countryFlags[key]}
                                  alt="flag"
                                  className="size-[1.3889vw] max-lg:size-5 rounded-full"
                                />

                              </div>
                              <span className="text-[0.9722vw] max-lg:text-[14px] text-black">
                                {name}
                              </span>
                            </div>

                            <span className="text-[0.9722vw] max-lg:text-[14px] text-[#00000066] uppercase">
                              {key.replace("_", " ")}
                            </span>
                          </div>
                        </li>
                      );
                    })
                  ) : (
                    <span className="text-center">{t("search.noResults")}</span>

                  )}
                </motion.ul>
              </div>
            </div>
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
